import { Slot, Slottable } from "@radix-ui/react-slot";
import { cva } from "class-variance-authority";
import { ArrowRight } from "lucide-react";
import * as React from "react";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-none text-base font-normal transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 cursor-pointer",
  {
    variants: {
      variant: {
        default: "bg-primary-400 text-primary-foreground hover:bg-primary",
        outline: "border border-input bg-transparent !text-white hover:bg-muted text-gray-700 hover:!text-foreground",
        white: "bg-white text-gray-700 hover:bg-gray-100",
        dark: "bg-background-dark text-white hover:bg-background-dark/80",
        link: "text-primary underline-offset-4 hover:underline !p-0",
        "link-secondary": "!text-foreground underline-offset-4 hover:underline !p-0 hover:text-primary-400",
        "link-white": "!p-0 text-white/80 hover:text-white hover:text-primary-400",
      },
      size: {
        default: "px-4 !py-2 text-[15px]",
        xs: "px-2 !py-1 text-[13px]",
        sm: "px-6 py-3 text-sm font-normal",
        lg: "px-8 py-4 text-lg",
        icon: "h-9 w-9",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

const Button = React.forwardRef(({ className, variant, size = "default", asChild = false, arrow = false, children, ...props }, ref) => {
  const Comp = asChild ? Slot : "button";

  if (arrow) {
    return (
      <Comp className={cn(buttonVariants({ variant, size }), "group gap-4", className)} ref={ref} {...props}>
        <Slottable>{children}</Slottable>
        <ArrowRight
          className={cn("group-hover:translate-x-1 transition-transform duration-300 ", {
            "size-5!": size === "lg",
            "size-4!": size === "default",
          })}
        />
      </Comp>
    );
  }

  return (
    <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props}>
      {children}
    </Comp>
  );
});
Button.displayName = "Button";

export { Button, buttonVariants };
